import { forwardRef } from 'react';
import { createPortal } from 'react-dom';
import type { MentionCandidate } from '../state/mentionUtils';
import type { MentionPopupPosition, SceneFramePosition } from './hooks/useMaterialMention';
import type { SceneFrameMentionSourceRow } from './utils';
import styles from './MaterialMentionPopup.module.css';

interface MaterialMentionPopupProps {
  candidates: MentionCandidate[];
  activeIndex: number;
  position: MentionPopupPosition | null;
  onSelect: (candidate: MentionCandidate) => void;
  onHoverIndex: (index: number) => void;
  /**
   * 「他シーンの動画」節の行 (docs/features/scene-video-frame-materials-visibility-plan.md §2.2)。
   * candidatesの後ろに続き、activeIndexはcandidates.lengthから数える。行クリックでは挿入せず、
   * 右端の[最初] [最後]ボタンで挿入する。
   */
  sceneFrameRows?: SceneFrameMentionSourceRow[];
  /** キーボードで選ばれている[最初]/[最後] (アクティブ行のボタンに強調を付ける)。 */
  sceneFramePosition?: SceneFramePosition;
  /** フレーム確保中の行とボタン。 */
  pendingSceneFrame?: { sceneId: string; position: SceneFramePosition } | null;
  onSelectSceneFrame?: (row: SceneFrameMentionSourceRow, position: SceneFramePosition) => void;
}

const SCENE_FRAME_BUTTONS: Array<{ position: SceneFramePosition; label: string }> = [
  { position: 'first', label: '最初' },
  { position: 'last', label: '最後' },
];

/**
 * 素材 @メンションの候補ポップアップ (docs/features/material-at-mention-plan.md P2)。
 * 極小サムネイル + バッジ文字 + 素材名 (あれば) を列挙する。候補ゼロなら呼び出し側
 * (useMaterialMention.isOpen) が false になるため、このコンポーネント自体は
 * 常に候補 1 件以上を渡される前提。
 *
 * document.body への portal で描画する (usePortalDropdown / SelectedAssetsPanel の
 * 動画参考モードメニューと同じ様式)。モーダル内の overflow クリッピングや
 * z-index 制約を受けないようにするため。
 */
export const MaterialMentionPopup = forwardRef<HTMLDivElement, MaterialMentionPopupProps>(
  function MaterialMentionPopup({
    candidates, activeIndex, position, onSelect, onHoverIndex,
    sceneFrameRows = [], sceneFramePosition = 'last', pendingSceneFrame = null, onSelectSceneFrame,
  }, ref) {
    const visibleSceneRows = onSelectSceneFrame ? sceneFrameRows : [];
    if (!position || (candidates.length === 0 && visibleSceneRows.length === 0)) return null;

    return createPortal(
      // data-scene-inline-edit: シーン説明のインライン編集 (useSceneActions) は
      // document mousedown の外側クリックで「保存して閉じる」ため、body へ portal した
      // このポップアップはマーカーが無いと「外側」扱いになり、行クリックの mousedown で
      // エディタごと消えて click (挿入) が届かない。マーカーで除外させる
      // (プロンプト欄など他ホストでは無害)。
      <div
        ref={ref}
        className={styles.popup}
        style={{ top: position.top, left: position.left }}
        role="listbox"
        data-scene-inline-edit
      >
        {candidates.map((candidate, index) => (
          <button
            key={`${candidate.kind}-${candidate.target.id}`}
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            className={`${styles.item} ${index === activeIndex ? styles.itemActive : ''}`}
            onMouseEnter={() => onHoverIndex(index)}
            // mousedown の既定動作 (textarea からのフォーカス移動) を抑止する。
            // blur 起点の保存・クローズ処理を持つホストでも挿入前にエディタが
            // 閉じないようにするため (click で onSelect が発火する)。
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(candidate)}
          >
            <span className={`${styles.thumb} ${candidate.kind === 'actor' ? styles.thumbActor : ''}`}>
              {candidate.target.thumbnailUrl && (
                <img src={candidate.target.thumbnailUrl} alt="" draggable={false} />
              )}
            </span>
            <span className={styles.badge}>{candidate.badge}</span>
            <span className={styles.name}>{candidate.target.name || '名称未設定'}</span>
          </button>
        ))}
        {visibleSceneRows.length > 0 && (
          <div className={styles.sectionLabel} role="presentation">他シーンの動画</div>
        )}
        {visibleSceneRows.map((row, rowIndex) => {
          const index = candidates.length + rowIndex;
          const isActive = index === activeIndex;
          const rowPending = pendingSceneFrame?.sceneId === row.sceneId;
          return (
            // 行クリックでは挿入しない (最初/最後のどちらか決まらないため)。divにして
            // ボタン以外の領域のクリックは何もしない。
            <div
              key={`scene-frame-${row.sceneId}`}
              role="option"
              aria-selected={isActive}
              className={`${styles.item} ${styles.sceneFrameItem} ${isActive ? styles.itemActive : ''}`}
              onMouseEnter={() => onHoverIndex(index)}
              onMouseDown={(e) => e.preventDefault()}
            >
              <span className={styles.thumb}>
                {row.thumbnailUrl && <img src={row.thumbnailUrl} alt="" draggable={false} />}
              </span>
              <span className={styles.name}>{row.label}</span>
              <span className={styles.sceneFrameButtons}>
                {SCENE_FRAME_BUTTONS.map(({ position: framePosition, label }) => {
                  const pending = rowPending && pendingSceneFrame?.position === framePosition;
                  const keyboardSelected = isActive && sceneFramePosition === framePosition;
                  return (
                    <button
                      key={framePosition}
                      type="button"
                      className={`${styles.sceneFrameButton} ${keyboardSelected ? styles.sceneFrameButtonSelected : ''}`}
                      disabled={!!pendingSceneFrame}
                      aria-busy={pending || undefined}
                      title={`${row.label}の${label}のフレームを参照`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onSelectSceneFrame?.(row, framePosition)}
                    >
                      {pending ? <span className={styles.spinner} aria-label="抽出中" /> : label}
                    </button>
                  );
                })}
              </span>
            </div>
          );
        })}
      </div>,
      document.body,
    );
  },
);
